import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import GabineteChip from "@/components/GabineteChip";
import {
  ArrowLeft, FloppyDisk, ArrowsClockwise, Warning,
  ListBullets, Files, Camera, FileMagnifyingGlass, ScrollIcon as ScrollFlat,
  Calculator, Buildings, VideoCamera, ChatCircleDots, X, PaperPlaneTilt, ShareNetwork,
  EnvelopeSimple, Robot, UserCircle, ClockCounterClockwise, DotsThreeVertical,
  CloudLightning, CaretDown, Car, CalendarBlank, Check,
  Plus, PencilSimple, Trash, FilmStrip, Bell,
} from "@phosphor-icons/react";
import DataTab from "@/components/expediente/DataTab";
import AseguradoLandingModal from "@/components/expediente/AseguradoLandingModal";
import DocsTab from "@/components/expediente/DocsTab";
import PhotosTab from "@/components/expediente/PhotosTab";
import VideosTab from "@/components/expediente/VideosTab";
import PolizaHistoricoTab from "@/components/expediente/PolizaHistoricoTab";
import InformeTab from "@/components/expediente/InformeTab";
import PrexistenciasTab from "@/components/expediente/PrexistenciasTab";
import ValoracionTab from "@/components/expediente/ValoracionTab";
import VideoperitajeTab from "@/components/expediente/VideoperitajeTab";
import TrackingTab from "@/components/expediente/TrackingTab";
import MeteoTab from "@/components/expediente/MeteoTab";
import RecordatoriosFeed from "@/components/gestores/RecordatoriosFeed";
import RepoCopilotDrawer from "@/components/expediente/RepoCopilotDrawer";
import AvizorLaunchModal from "@/components/expediente/AvizorLaunchModal";
import DerivarModal from "@/components/expediente/DerivarModal";
import CitaNotifyModal from "@/components/expediente/CitaNotifyModal";  // exporta VisitaPrevistaModal
import { useChat } from "@/contexts/ChatContext";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const TABS = [
  { k: "datos", label: "Expediente", icon: ListBullets, mobile: true, primary: true },
  { k: "docs", label: "Documentos", icon: Files, mobile: true, primary: true },
  { k: "fotos", label: "Fotos", icon: Camera, mobile: true, primary: true },
  { k: "videos", label: "Vídeos", icon: FilmStrip, mobile: true, primary: true },
  { k: "poliza", label: "Otros de la póliza", icon: FileMagnifyingGlass, mobile: false, primary: true },
  { k: "informe", label: "Informe", icon: ScrollFlat, mobile: false, primary: true },
  { k: "valoracion", label: "Valoración", icon: Calculator, mobile: false, primary: true },
  // Secundarias — en el dropdown "Más"
  { k: "prexistencias", label: "Prexistencias", icon: Buildings, mobile: false, primary: false },
  { k: "videoperitaje", label: "Videoperitaje", icon: VideoCamera, mobile: true, primary: true },
  { k: "meteo", label: "Meteo", icon: CloudLightning, mobile: true, primary: false },
  { k: "recordatorios", label: "Recordatorios", icon: Bell, mobile: false, primary: false },
  { k: "tracking", label: "Tracking", icon: ClockCounterClockwise, mobile: false, primary: false },
];

const MOBILE_BLOCKED_MSG =
  "Esta pestaña solo está disponible en escritorio. Pídeselo a un compañero o ábrelo desde el ordenador.";

export default function Review() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshConversations } = useChat();
  const { user } = useAuth();
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [landingModalOpen, setLandingModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalPrefill, setEmailModalPrefill] = useState(null);
  const [avizorModalOpen, setAvizorModalOpen] = useState(false);
  const [derivarModalOpen, setDerivarModalOpen] = useState(false);
  const [citaModalOpen, setCitaModalOpen] = useState(false);
  const [exp, setExp] = useState(null);
  const [data, setData] = useState({});
  const [peritos, setPeritos] = useState([]);
  const [gabinete, setGabinete] = useState(null);
  const [tab, setTab] = useState("datos");
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const moreTriggerRef = useRef(null);
  const [moreMenuPos, setMoreMenuPos] = useState(null); // {top, right} cuando abre
  // Cerrar el dropdown "Más" al hacer click fuera o pulsar Escape.
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => {
      // Como el menú está renderizado vía portal (fuera de moreRef), también
      // hay que ignorar clicks dentro del propio menú. Usamos el atributo
      // data-tab-more-menu para identificarlo.
      if (moreRef.current && moreRef.current.contains(e.target)) return;
      if (e.target.closest?.('[data-tab-more-menu="1"]')) return;
      setMoreOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    const reposition = () => {
      if (!moreTriggerRef.current) return;
      const r = moreTriggerRef.current.getBoundingClientRect();
      setMoreMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    };
    reposition();
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [moreOpen]);
  const [counts, setCounts] = useState({ docs: 0, photos: 0, videos: 0, poliza: 0 });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | dirty | saving | saved | error
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  // Refs para autosave
  const autosaveTimerRef = useRef(null);
  const isFirstDataLoadRef = useRef(true);
  const lastSavedDataRef = useRef(null);

  // Copiloto del repositorio (drawer lateral)
  const [copilotOpen, setCopilotOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Permite navegar a /review/{id}?tab=videoperitaje desde otras pantallas
  // (ej. AvizorListener tras aceptar una videoperitación inmediata).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && TABS.some((x) => x.k === t)) setTab(t);
  }, [id]);

  const loadExp = async () => {
    const r = await api.get(`/expedientes/${id}`);
    setExp(r.data);
    const ed = r.data.extracted_data || {};
    if (Object.keys(data).length === 0) {
      setData(ed);
      lastSavedDataRef.current = JSON.stringify(ed);
    } else {
      // Sincronizar campos que el backend puede cambiar atómicamente
      // (asignación/desasignación de perito, derivar a presencial/videoperitación).
      setData((d) => ({ ...d, perito_codigo: ed.perito_codigo, tipo_encargo: ed.tipo_encargo }));
      // Mantener el snapshot "lastSaved" actualizado para que el autosave
      // no detecte un falso "dirty" tras la mutación remota.
      lastSavedDataRef.current = JSON.stringify({ ...data, perito_codigo: ed.perito_codigo, tipo_encargo: ed.tipo_encargo });
    }
  };

  const loadCounts = async () => {
    try {
      const [d, p, v, pol] = await Promise.all([
        api.get(`/expedientes/${id}/attachments?kind=docs`),
        api.get(`/expedientes/${id}/attachments?kind=photo`),
        api.get(`/expedientes/${id}/attachments?kind=video`),
        api.get(`/expedientes/${id}/otros-siniestros-poliza`).catch(() => ({ data: { total: 0 } })),
      ]);
      setCounts({
        docs: d.data.length,
        photos: p.data.length,
        videos: v.data.length,
        poliza: pol.data?.total || 0,
      });
    } catch (e) { console.warn("[review] loadCounts:", e?.message); }
  };

  useEffect(() => {
    // Reset COMPLETO al cambiar de expediente (la búsqueda global navega
    // de un /review/A → /review/B reutilizando el mismo componente). Si
    // no limpiamos `data`, `gabinete`, `counts` y los refs de autosave,
    // el form muestra los datos del expediente anterior y el autosave
    // los persiste sobre el nuevo expediente (bug grave de mezcla).
    setExp(null);
    setData({});
    setGabinete(null);
    setCounts({ docs: 0, photos: 0, videos: 0, poliza: 0 });
    isFirstDataLoadRef.current = true;
    lastSavedDataRef.current = null;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    setAutosaveStatus("idle");

    loadExp();
    loadCounts();
    api.get("/peritos").then((r) => setPeritos(r.data));
    const i = setInterval(async () => {
      const r = await api.get(`/expedientes/${id}`);
      setExp(r.data);
    }, 4000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [id]);

  // Carga del gabinete asociado al expediente (para pintar el chip de
  // etiqueta corta junto al título). Se re-fetcha cada vez que cambia el
  // `gabinete_id` (al cambiarlo desde el detalle).
  useEffect(() => {
    if (!exp?.gabinete_id) { setGabinete(null); return; }
    let cancelled = false;
    api.get(`/gabinetes/${exp.gabinete_id}`)
      .then((r) => { if (!cancelled) setGabinete(r.data); })
      .catch(() => { if (!cancelled) setGabinete(null); });
    return () => { cancelled = true; };
  }, [exp?.gabinete_id]);

  const onChangeData = (k, v) => setData({ ...data, [k]: v });

  // ===================================================================
  // Autoguardado con debounce 1s. Cuando cambia `data` (extracted_data),
  // tras 1s de inactividad, se hace PUT al backend.
  // ===================================================================
  useEffect(() => {
    if (!exp) return;
    if (Object.keys(data).length === 0) return;

    const serialized = JSON.stringify(data);

    // Primer load → marca el snapshot inicial sin disparar guardado.
    if (isFirstDataLoadRef.current) {
      isFirstDataLoadRef.current = false;
      lastSavedDataRef.current = serialized;
      return;
    }
    // Sin cambios reales → nada que hacer.
    if (serialized === lastSavedDataRef.current) return;

    setAutosaveStatus("dirty");

    // Re-armar el debounce
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      setAutosaveStatus("saving");
      try {
        await api.put(`/expedientes/${id}`, { extracted_data: data });
        lastSavedDataRef.current = serialized;
        setSavedAt(new Date());
        setAutosaveStatus("saved");
        // Vuelve a "idle" pasados 2s
        setTimeout(() => {
          setAutosaveStatus((s) => (s === "saved" ? "idle" : s));
        }, 2000);
      } catch {
        setAutosaveStatus("error");
      }
    }, 1000);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const updateExpedienteField = async (k, v) => {
    setExp({ ...exp, [k]: v });
    try {
      await api.put(`/expedientes/${id}`, { [k]: v });
    } catch (e) { console.warn("[review] updateExpedienteField:", e?.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/expedientes/${id}`, { extracted_data: data });
      setSavedAt(new Date());
      loadExp();
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const msg = dryRun
      ? "DRY RUN — el bot rellenará el formulario en Dobilas y capturará un screenshot SIN enviarlo. ¿Continuar?"
      : "ENVÍO REAL — el bot creará el expediente en Dobilas. ¿Continuar?";
    if (!confirm(msg)) return;
    setSubmitting(true);
    try {
      await api.put(`/expedientes/${id}`, { extracted_data: data });
      await api.post(`/expedientes/${id}/submit?dry_run=${dryRun}`);
      loadExp();
    } finally {
      setSubmitting(false);
    }
  };

  const retry = async () => {
    if (exp.status !== "error" && !confirm("¿Forzar re-extracción del parte? Los datos extraídos se sobrescribirán con la nueva lectura del PDF original. Los cambios manuales que hayas hecho se conservarán como están guardados, salvo los que coincidan con campos detectados de nuevo por la IA.")) {
      return;
    }
    try {
      await api.post(`/expedientes/${id}/retry-extraction`);
      toast.success("Re-extracción lanzada — actualiza en unos segundos");
      loadExp();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error lanzando re-extracción");
    }
  };

  const openExpedienteChat = () => setChatModalOpen(true);

  if (!exp) return <div className="p-10 text-muted text-sm">Cargando...</div>;

  const isReady = exp.status === "ready_to_review";
  const isProcessing = exp.status === "extracting" || exp.status === "submitting" || exp.status === "pending";

  const tabCount = (k) =>
    k === "docs" ? counts.docs :
    k === "fotos" ? counts.photos :
    k === "videos" ? counts.videos :
    k === "poliza" ? counts.poliza :
    null;

  // Construir título del expediente
  const title = exp.extracted_data?.numero_siniestro
    ? `${exp.extracted_data?.aseguradora || "—"} · ${exp.extracted_data.numero_siniestro}`
    : exp.source_filename;

  return (
    // El padre directo es `<main>` de Layout.jsx, que tiene un topbar
    // sticky de 54px arriba. Si usamos `h-screen` (100vh), el bottom queda
    // 54px por debajo del viewport y el contenido interno se corta. Con
    // `calc(100vh-54px)` la página encaja exactamente en el hueco visible
    // bajo la barra de búsqueda global y los scrollers internos pueden
    // mostrar todo su contenido.
    <div className="flex flex-col h-[calc(100vh-54px)]" data-testid="review-page">
      {/* Header */}
      <header className="border-b border-border bg-white flex-shrink-0">
        <div className="min-h-[68px] px-3 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 border border-border rounded-lg flex items-center justify-center text-muted hover:text-foreground hover:bg-background transition-colors flex-shrink-0"
              data-testid="back-button"
              title="Volver"
            >
              <ArrowLeft className="w-4 h-4" weight="regular" />
            </button>
            <div className="min-w-0">
              <div className="text-[11px] text-muted hidden md:block leading-tight">Expediente</div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="font-display text-[19px] font-semibold tracking-tight truncate text-foreground leading-tight">{title}</h1>
                <GabineteChip gabinete={gabinete} size="md" />
                <StatusBadge status={exp.status} />
                <AutosaveIndicator status={autosaveStatus} savedAt={savedAt} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setCopilotOpen(true)}
              data-testid="open-repo-copilot"
              title="Copiloto IA — pregúntale a la documentación de la aseguradora"
              className="text-[13px] px-3 h-9 border border-border bg-white rounded-lg hover:bg-background inline-flex items-center gap-1.5 text-foreground transition-colors"
            >
              <Robot className="w-4 h-4 text-muted" weight="regular" />
              <span className="hidden md:inline">Copiloto IA</span>
            </button>
            {can.launchAvizor(user) && (
              <button
                onClick={() => setAvizorModalOpen(true)}
                data-testid="open-avizor-modal"
                title="Lanzar videoperitación inmediata (Avizor) al perito asignado"
                className="text-[13px] px-3 h-9 border border-border bg-white rounded-lg hover:bg-background inline-flex items-center gap-1.5 text-foreground transition-colors"
              >
                <VideoCamera className="w-4 h-4 text-muted" weight="regular" />
                <span className="hidden md:inline">Avizor</span>
              </button>
            )}
            <button
              onClick={openExpedienteChat}
              data-testid="open-expediente-chat"
              title="Abrir chat sobre este expediente"
              className="text-[13px] px-3 h-9 border border-border bg-white rounded-lg hover:bg-background inline-flex items-center gap-1.5 text-foreground transition-colors"
            >
              <ChatCircleDots className="w-4 h-4 text-muted" weight="regular" />
              <span className="hidden md:inline">Chat</span>
            </button>

            {/* Visita prevista — acción principal del flujo de cita */}
            <button
              onClick={() => setCitaModalOpen(true)}
              data-testid="header-visita-prevista"
              title="Concertar fecha y hora de visita (agenda automática del perito)"
              className={`text-[13px] px-3 h-9 rounded-lg inline-flex items-center gap-1.5 transition-colors ${
                exp?.visita_prevista_at
                  ? "bg-emerald-50 border border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                  : "bg-orange-50 border border-orange-300 text-orange-900 hover:bg-orange-100"
              }`}
            >
              <CalendarBlank className="w-4 h-4" weight={exp?.visita_prevista_at ? "fill" : "regular"} />
              <span className="hidden md:inline">
                {exp?.visita_prevista_at
                  ? `Visita ${new Date(exp.visita_prevista_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })} ${exp.visita_prevista_at.slice(11, 16)}`
                  : "Visita prevista"}
              </span>
            </button>

            {/* Menú "Más" con acciones poco usadas */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="open-more-actions"
                  title="Más acciones"
                  className="w-9 h-9 border border-border bg-white rounded-lg hover:bg-background inline-flex items-center justify-center text-foreground transition-colors"
                >
                  <DotsThreeVertical className="w-5 h-5" weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {!isProcessing && (
                  <DropdownMenuItem onClick={retry} data-testid="more-retry-extraction" className="text-[13px] cursor-pointer">
                    <ArrowsClockwise className="w-4 h-4 mr-2 text-muted" weight="regular" />
                    {exp.status === "error" ? "Reintentar IA" : "Forzar OCR"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setShareModalOpen(true)} data-testid="more-share" className="text-[13px] cursor-pointer">
                  <ShareNetwork className="w-4 h-4 mr-2 text-muted" weight="regular" />
                  Compartir
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLandingModalOpen(true)} data-testid="more-landing" className="text-[13px] cursor-pointer">
                  <UserCircle className="w-4 h-4 mr-2 text-muted" weight="regular" />
                  Enlace asegurado
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDerivarModalOpen(true)} data-testid="more-derivar" className="text-[13px] cursor-pointer">
                  <Car className="w-4 h-4 mr-2 text-muted" weight="regular" />
                  {String(data?.tipo_encargo || "").toLowerCase().startsWith("presencial")
                    ? "Volver a videoperitación"
                    : "Derivar a presencial"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCitaModalOpen(true)} data-testid="more-cita" className="text-[13px] cursor-pointer">
                  <CalendarBlank className="w-4 h-4 mr-2 text-muted" weight="regular" />
                  {exp?.visita_prevista_at ? "Editar visita prevista" : "Visita prevista"}
                </DropdownMenuItem>
                {exp?.visita_prevista_at && (
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        if (exp?.visita_hecha_at) {
                          await api.post(`/expedientes/${exp.id}/visita-hecha`, { unmark: true });
                          toast.success("Visita marcada como NO realizada");
                        } else {
                          await api.post(`/expedientes/${exp.id}/visita-hecha`, {});
                          toast.success("Visita marcada como realizada");
                        }
                        loadExp();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "No se pudo actualizar");
                      }
                    }}
                    data-testid="more-visita-hecha"
                    className="text-[13px] cursor-pointer"
                  >
                    <Check className="w-4 h-4 mr-2 text-muted" weight="regular" />
                    {exp?.visita_hecha_at ? "Desmarcar visita hecha" : "Marcar visita como hecha"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setEmailModalOpen(true)} data-testid="more-email" className="text-[13px] cursor-pointer">
                  <EnvelopeSimple className="w-4 h-4 mr-2 text-muted" weight="regular" />
                  Enviar email
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={save}
              disabled={saving || isProcessing}
              data-testid="save-button"
              className="text-[13px] px-3 h-9 rounded-lg inline-flex items-center gap-1.5 bg-brand hover:bg-brand/90 text-white font-semibold disabled:opacity-50 transition-colors"
            >
              <FloppyDisk className="w-4 h-4" weight="regular" /> <span className="hidden md:inline">Guardar</span>
            </button>
          </div>
        </div>

        {/* Tabs — solo primarias visibles; el resto en menú "Más".
            Activa: subrayado naranja 2px + texto naranja semibold.
            Inactiva: gris, sin subrayado. Contadores en pastilla gris pequeña. */}
        <div className="px-3 md:px-6 flex items-center gap-0 overflow-x-auto no-scrollbar">
          {TABS.filter((t) => t.primary).map((t) => {
            const isActive = tab === t.k;
            const Icon = t.icon;
            const c = tabCount(t.k);
            const blockedOnMobile = isMobile && !t.mobile;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                title={blockedOnMobile ? "Solo en escritorio" : undefined}
                className={`px-3 md:px-4 py-2.5 text-[13px] border-b-2 inline-flex items-center gap-2 transition-colors whitespace-nowrap -mb-px ${
                  isActive
                    ? "border-brand text-brand font-semibold"
                    : `border-transparent font-medium ${blockedOnMobile ? "text-slate-300" : "text-muted hover:text-foreground"}`
                }`}
              >
                <Icon className="w-4 h-4" weight="regular" />
                {t.label}
                {c !== null && c > 0 && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                    t.k === "poliza"
                      ? "bg-amber-100 text-amber-800 border border-amber-200"
                      : "bg-surface-2 text-muted"
                  }`}>
                    {c}
                  </span>
                )}
              </button>
            );
          })}

          {/* Botón "Más" + dropdown con las pestañas secundarias.
              Si la activa es secundaria, el botón "Más" hereda el estilo activo
              (subrayado naranja + naranja semibold) y muestra el nombre.
              El menú se renderiza vía portal para evitar que el overflow-x-auto
              del contenedor padre lo recorte verticalmente. */}
          <div className="relative -mb-px" ref={moreRef}>
            {(() => {
              const secondaries = TABS.filter((t) => !t.primary);
              const activeSecondary = secondaries.find((t) => t.k === tab);
              const isActive = !!activeSecondary;
              const label = activeSecondary ? activeSecondary.label : "Más";
              return (
                <button
                  ref={moreTriggerRef}
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  data-testid="tab-more-button"
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  className={`px-3 md:px-4 py-2.5 text-[13px] border-b-2 inline-flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-brand text-brand font-semibold"
                      : "border-transparent font-medium text-muted hover:text-foreground"
                  }`}
                >
                  {activeSecondary
                    ? <activeSecondary.icon className="w-4 h-4" weight="regular" />
                    : null}
                  <span>{label}</span>
                  <CaretDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} weight="bold" />
                </button>
              );
            })()}

            {moreOpen && moreMenuPos && createPortal(
              <div
                role="menu"
                data-testid="tab-more-menu"
                data-tab-more-menu="1"
                style={{ position: "fixed", top: moreMenuPos.top, right: moreMenuPos.right }}
                className="w-60 bg-white border border-border rounded-lg shadow-lg z-[60] py-1 overflow-hidden"
              >
                {TABS.filter((t) => !t.primary).map((t) => {
                  const isActive = tab === t.k;
                  const Icon = t.icon;
                  const c = tabCount(t.k);
                  const blockedOnMobile = isMobile && !t.mobile;
                  return (
                    <button
                      key={t.k}
                      role="menuitem"
                      onClick={() => { setTab(t.k); setMoreOpen(false); }}
                      data-testid={`tab-${t.k}`}
                      className={`w-full px-3 py-2 text-left text-[13px] inline-flex items-center gap-2 transition-colors ${
                        isActive
                          ? "bg-brand-soft text-brand-fg font-semibold"
                          : blockedOnMobile
                          ? "text-slate-300"
                          : "text-foreground hover:bg-surface-2"
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" weight="regular" />
                      <span className="flex-1 truncate">{t.label}</span>
                      {c !== null && c > 0 && (
                        <span className="text-[10.5px] px-1.5 py-0.5 rounded-full font-semibold bg-surface-2 text-muted">
                          {c}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
          </div>
        </div>
      </header>

      {exp.error_message && (
        <div className="px-6 py-3 bg-status-error-bg border-b border-status-error-bd text-status-error-fg text-sm flex items-start gap-2">
          <Warning className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />
          <div>
            <div className="font-semibold">Aviso</div>
            <div className="text-xs">{exp.error_message}</div>
          </div>
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {(() => {
          const currentTab = TABS.find((t) => t.k === tab);
          if (isMobile && currentTab && !currentTab.mobile) {
            return (
              <div className="h-full flex items-center justify-center p-6">
                <div className="text-center max-w-xs">
                  <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 inline-flex items-center justify-center mb-3">
                    <Warning className="w-6 h-6" weight="bold" />
                  </div>
                  <h3 className="font-display font-bold text-base mb-1">
                    Solo disponible en escritorio
                  </h3>
                  <p className="text-[12px] text-muted leading-relaxed">
                    {MOBILE_BLOCKED_MSG}
                  </p>
                  <button
                    onClick={() => setTab("datos")}
                    className="mt-4 text-[12px] px-3 py-1.5 bg-foreground text-white font-semibold rounded-md"
                  >
                    Volver al expediente
                  </button>
                </div>
              </div>
            );
          }
          return null;
        })()}
        {(!isMobile || TABS.find((t) => t.k === tab)?.mobile) && (
          <>
            {tab === "datos" && (
              <DataTab
                exp={exp}
                data={data}
                onChange={onChangeData}
                peritos={peritos}
                isProcessing={isProcessing}
                onUpdateExpediente={updateExpedienteField}
                onReload={loadExp}
                onOpenVisita={() => setCitaModalOpen(true)}
              />
            )}
            {tab === "docs" && (
              <DocsTab
                expedienteId={id}
                onCountChange={loadCounts}
                onSendEmail={({ attachmentIds, files }) => {
                  setEmailModalPrefill({ attachmentIds, files });
                  setEmailModalOpen(true);
                }}
              />
            )}
            {tab === "fotos" && <PhotosTab expedienteId={id} onCountChange={loadCounts} />}
            {tab === "videos" && <VideosTab expedienteId={id} onCountChange={loadCounts} />}
            {tab === "informe" && <InformeTab exp={exp} onReload={loadExp} />}
            {tab === "prexistencias" && <PrexistenciasTab exp={exp} onReload={loadExp} />}
            {tab === "valoracion" && <ValoracionTab exp={exp} onReload={loadExp} />}
            {/* Tabs sin scroller propio: los envolvemos para que su contenido
                pueda hacer scroll completo dentro del hueco del expediente. */}
            {tab === "videoperitaje" && (
              <div className="h-full overflow-y-auto"><VideoperitajeTab exp={exp} onExpReload={loadExp} /></div>
            )}
            {tab === "poliza" && (
              <div className="h-full overflow-y-auto"><PolizaHistoricoTab expedienteId={id} /></div>
            )}
            {tab === "meteo" && (
              <div className="h-full overflow-y-auto"><MeteoTab exp={exp} /></div>
            )}
            {tab === "tracking" && (
              <div className="h-full overflow-y-auto p-3 md:p-6"><TrackingTab expedienteId={id} /></div>
            )}
            {tab === "recordatorios" && (
              <div className="h-full overflow-y-auto p-3 md:p-6">
                <RecordatoriosFeed expediente={exp} currentUser={user} />
              </div>
            )}
          </>
        )}
      </div>

      {chatModalOpen && (
        <ExpedienteChatModal
          expediente={exp}
          currentUser={user}
          onClose={() => setChatModalOpen(false)}
          onCreated={async (convId) => {
            await refreshConversations();
            setChatModalOpen(false);
            navigate("/chat", { state: { conversationId: convId } });
          }}
        />
      )}
      {shareModalOpen && (
        <ShareExpedienteModal
          expediente={exp}
          onClose={() => setShareModalOpen(false)}
        />
      )}
      {landingModalOpen && (
        <AseguradoLandingModal
          expediente={exp}
          onClose={() => setLandingModalOpen(false)}
        />
      )}
      {emailModalOpen && (
        <ExpedienteEmailModal
          expediente={exp}
          prefill={emailModalPrefill}
          onClose={() => { setEmailModalOpen(false); setEmailModalPrefill(null); }}
        />
      )}
      {avizorModalOpen && (
        <AvizorLaunchModal
          expediente={exp}
          peritos={peritos}
          onClose={() => setAvizorModalOpen(false)}
          onLaunched={() => setTab("videoperitaje")}
        />
      )}
      {derivarModalOpen && (
        <DerivarModal
          expediente={exp}
          peritos={peritos}
          onClose={() => setDerivarModalOpen(false)}
          onDerived={() => loadExp()}
        />
      )}
      {citaModalOpen && (
        <CitaNotifyModal
          expediente={exp}
          onClose={() => setCitaModalOpen(false)}
          onSaved={() => loadExp()}
        />
      )}

      <RepoCopilotDrawer
        open={copilotOpen}
        onClose={() => setCopilotOpen(false)}
        expediente={exp}
      />
    </div>
  );
}


function ExpedienteEmailModal({ expediente, prefill, onClose }) {
  const { user } = useAuth();
  const ed = expediente?.extracted_data || {};
  const numero = ed.numero_expediente || expediente?.id?.slice(0, 8);
  const asegurado = ed.asegurado_nombre || "";
  const siniestroCia = ed.numero_siniestro || "";
  const refGabinete = ed.numero_siniestro_gabinete || ed.numero_expediente || "";

  // Asunto por defecto solicitado por el usuario:
  //   Sin.: {numero_siniestro} - {direccion_riesgo} - {cp} {municipio} - Pól.: {numero_poliza}
  // Cualquier campo que falte se omite limpiamente sin dejar guiones huérfanos.
  const buildDefaultSubject = () => {
    const parts = [];
    if (ed.numero_siniestro) parts.push(`Sin.: ${ed.numero_siniestro}`);
    const cpMun = [ed.codigo_postal, ed.municipio].filter(Boolean).join(" ");
    const direccion = [ed.direccion_riesgo, cpMun].filter(Boolean).join(" - ");
    if (direccion) parts.push(direccion);
    if (ed.numero_poliza) parts.push(`Pól.: ${ed.numero_poliza}`);
    if (parts.length) return parts.join(" - ");
    // Fallback si no hay datos extraídos.
    return `[Expediente ${numero}]${asegurado ? ` ${asegurado}` : ""}`;
  };
  const subjectDefault = buildDefaultSubject();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(subjectDefault);
  const [body, setBody] = useState("");
  const [attachInforme, setAttachInforme] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState(prefill?.files || []);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTplId, setSelectedTplId] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDraft, setEditorDraft] = useState(null); // {id?, name, subject, body}
  const [tplBusy, setTplBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/me/email-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const removeAttachment = (id) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
  };
  const totalSize = attachedFiles.reduce((a, f) => a + (f.size_bytes || 0), 0);
  const fmtSize = (b) => (b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(2) + " MB" : (b / 1024).toFixed(0) + " KB");

  const applyTemplate = (t) => {
    if (!t) { setSelectedTplId(""); return; }
    setSelectedTplId(t.id);
    // Contexto de interpolación para las plantillas.
    // `numero` = nº de siniestro de compañía (es lo que tiene sentido para
    // un destinatario externo, no el id interno del expediente).
    // `ref_gabinete` = nuestro nº de siniestro / referencia interna.
    const ctx = {
      numero: siniestroCia || refGabinete || "",     // legacy: alias del más relevante
      numero_siniestro: siniestroCia,
      ref_gabinete: refGabinete,
      asegurado: ed.asegurado_nombre || "",
      aseguradora: ed.aseguradora_nombre || ed.compania || "",
      poliza: ed.poliza || ed.numero_poliza || "",
      siniestro: siniestroCia,
      direccion: ed.direccion_riesgo || "",
      municipio: ed.municipio || "",
      cp: ed.codigo_postal || "",
      firma: user?.name || "",
    };
    const interp = (s) => (s || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => ctx[k] ?? "");
    if (t.subject) setSubject(interp(t.subject));
    setBody(interp(t.body));
  };

  // Plantillas — CRUD inline ----------------------------------------------
  const openTemplateEditor = (mode) => {
    if (mode === "edit") {
      const t = templates.find((x) => x.id === selectedTplId);
      if (!t) { toast.error("Selecciona una plantilla primero"); return; }
      setEditorDraft({ id: t.id, name: t.name, subject: t.subject || "", body: t.body || "" });
    } else if (mode === "new") {
      setEditorDraft({ id: null, name: "", subject: "", body: "" });
    } else if (mode === "save-as") {
      // Guardar lo que tengo en el modal como nueva plantilla.
      setEditorDraft({ id: null, name: "", subject: subject || "", body: body || "" });
    }
    setEditorOpen(true);
  };

  const saveTemplate = async () => {
    if (!editorDraft) return;
    const name = (editorDraft.name || "").trim();
    const body = (editorDraft.body || "").trim();
    if (!name || !body) { toast.error("Nombre y cuerpo son obligatorios"); return; }
    setTplBusy(true);
    try {
      if (editorDraft.id) {
        const r = await api.patch(`/settings/me/email-templates/${editorDraft.id}`, {
          name, subject: editorDraft.subject, body,
        });
        setTemplates((prev) => prev.map((t) => (t.id === r.data.id ? r.data : t)));
        toast.success("Plantilla actualizada");
        setSelectedTplId(r.data.id);
      } else {
        const r = await api.post("/settings/me/email-templates", {
          name, subject: editorDraft.subject, body,
        });
        setTemplates((prev) => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Plantilla creada");
        setSelectedTplId(r.data.id);
      }
      setEditorOpen(false);
      setEditorDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la plantilla");
    } finally {
      setTplBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (!selectedTplId) return;
    const t = templates.find((x) => x.id === selectedTplId);
    if (!t) return;
    if (!window.confirm(`¿Eliminar la plantilla "${t.name}"?`)) return;
    try {
      await api.delete(`/settings/me/email-templates/${selectedTplId}`);
      setTemplates((prev) => prev.filter((x) => x.id !== selectedTplId));
      setSelectedTplId("");
      toast("Plantilla eliminada");
    } catch (e) {
      // Si la plantilla era "por defecto" no persistida, el server devuelve
      // 404. Avisamos pero retiramos del listado igualmente.
      if (e?.response?.status === 404) {
        toast("La plantilla por defecto se ha ocultado");
        setTemplates((prev) => prev.filter((x) => x.id !== selectedTplId));
        setSelectedTplId("");
      } else {
        toast.error(e?.response?.data?.detail || "No se pudo eliminar");
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim()) { toast.error("Indica un destinatario"); return; }
    if (!body.trim()) { toast.error("Escribe el mensaje"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/expedientes/${expediente.id}/send-email`, {
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject: subject.trim() || subjectDefault,
        body,
        attach_informe: attachInforme,
        attachment_ids: attachedFiles.map((f) => f.id),
      });
      if (r.data?.ok) {
        toast.success(`Correo enviado a ${to}`);
        onClose();
      } else if (r.data?.reason === "smtp-not-configured") {
        toast.error("SMTP no configurado. Configúralo en /admin/configuracion → Email.");
      } else {
        toast.error(r.data?.error || "No se pudo enviar el correo");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error de red");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="expediente-email-modal">
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg inline-flex items-center gap-2">
            <EnvelopeSimple className="w-5 h-5 text-amber-700" weight="duotone" />
            Enviar email del expediente
          </h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          <div className="text-[11px] text-muted bg-surface/50 border border-border rounded-md px-3 py-2 leading-relaxed">
            {siniestroCia && <><strong className="text-foreground">Siniestro compañía:</strong> {siniestroCia}</>}
            {siniestroCia && refGabinete && <> · </>}
            {refGabinete && <><strong className="text-foreground">Ref. gabinete:</strong> {refGabinete}</>}
            {!siniestroCia && !refGabinete && <><strong className="text-foreground">Expediente:</strong> {numero}</>}
            {asegurado && <><br/><strong className="text-foreground">Asegurado:</strong> {asegurado}</>}
            {ed.aseguradora_nombre && <> · <strong className="text-foreground">Aseguradora:</strong> {ed.aseguradora_nombre}</>}
          </div>
          {/* Plantillas con editor inline */}
          <div className="space-y-2" data-testid="exp-email-templates-block">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-muted">Plantilla rápida</span>
              <button
                type="button"
                onClick={() => openTemplateEditor("new")}
                title="Crear plantilla nueva"
                data-testid="tpl-new-btn"
                className="ml-auto text-[10.5px] font-semibold text-brand hover:underline inline-flex items-center gap-0.5"
              >
                <Plus className="w-3 h-3" weight="bold" /> Nueva
              </button>
              {body.trim() && (
                <button
                  type="button"
                  onClick={() => openTemplateEditor("save-as")}
                  title="Guardar el contenido actual como plantilla"
                  data-testid="tpl-save-as-btn"
                  className="text-[10.5px] font-semibold text-brand hover:underline"
                >
                  Guardar como…
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <select
                value={selectedTplId}
                onChange={(e) => applyTemplate(templates.find((t) => t.id === e.target.value))}
                data-testid="exp-email-template"
                className="flex-1 text-xs border border-border rounded-md px-3 py-2 bg-white"
              >
                <option value="">— Escribir desde cero —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => openTemplateEditor("edit")}
                disabled={!selectedTplId}
                title="Editar plantilla seleccionada"
                data-testid="tpl-edit-btn"
                className="h-9 w-9 inline-flex items-center justify-center border border-border rounded-md text-muted hover:text-foreground hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <PencilSimple className="w-4 h-4" weight="regular" />
              </button>
              <button
                type="button"
                onClick={deleteTemplate}
                disabled={!selectedTplId}
                title="Eliminar plantilla seleccionada"
                data-testid="tpl-delete-btn"
                className="h-9 w-9 inline-flex items-center justify-center border border-border rounded-md text-muted hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Trash className="w-4 h-4" weight="regular" />
              </button>
            </div>

            {editorOpen && editorDraft && (
              <div className="border border-amber-200 bg-amber-50/40 rounded-md p-3 space-y-2" data-testid="tpl-editor">
                <div className="flex items-center justify-between">
                  <h4 className="text-[12px] font-bold text-foreground">
                    {editorDraft.id ? "Editar plantilla" : "Nueva plantilla"}
                  </h4>
                  <button type="button" onClick={() => { setEditorOpen(false); setEditorDraft(null); }} className="text-muted hover:text-foreground" data-testid="tpl-editor-close">
                    <X className="w-3.5 h-3.5" weight="bold" />
                  </button>
                </div>
                <label className="block">
                  <span className="text-[10.5px] font-semibold text-muted block mb-0.5">Nombre *</span>
                  <input
                    value={editorDraft.name}
                    onChange={(e) => setEditorDraft({ ...editorDraft, name: e.target.value })}
                    placeholder="Ej. Solicitud al reparador"
                    data-testid="tpl-editor-name"
                    className="w-full text-xs border border-border rounded-md px-3 py-1.5 bg-white"
                  />
                </label>
                <label className="block">
                  <span className="text-[10.5px] font-semibold text-muted block mb-0.5">Asunto</span>
                  <input
                    value={editorDraft.subject}
                    onChange={(e) => setEditorDraft({ ...editorDraft, subject: e.target.value })}
                    placeholder="Sin. {{numero_siniestro}} — …"
                    data-testid="tpl-editor-subject"
                    className="w-full text-xs border border-border rounded-md px-3 py-1.5 bg-white font-mono"
                  />
                </label>
                <label className="block">
                  <span className="text-[10.5px] font-semibold text-muted block mb-0.5">Cuerpo *</span>
                  <textarea
                    value={editorDraft.body}
                    onChange={(e) => setEditorDraft({ ...editorDraft, body: e.target.value })}
                    rows={6}
                    placeholder={"Hola,\n\nTe escribo en relación con el siniestro {{numero_siniestro}}..."}
                    data-testid="tpl-editor-body"
                    className="w-full text-xs border border-border rounded-md px-3 py-1.5 bg-white"
                  />
                </label>
                <div className="text-[10px] text-muted leading-snug">
                  Variables disponibles: <code>{"{{numero_siniestro}}"}</code> · <code>{"{{ref_gabinete}}"}</code> · <code>{"{{asegurado}}"}</code> · <code>{"{{aseguradora}}"}</code> · <code>{"{{poliza}}"}</code> · <code>{"{{direccion}}"}</code> · <code>{"{{municipio}}"}</code> · <code>{"{{cp}}"}</code> · <code>{"{{firma}}"}</code>
                </div>
                <div className="flex justify-end gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={() => { setEditorOpen(false); setEditorDraft(null); }}
                    className="text-[11.5px] font-semibold px-3 py-1.5 rounded-md text-muted hover:bg-zinc-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={saveTemplate}
                    disabled={tplBusy}
                    data-testid="tpl-editor-save"
                    className="text-[11.5px] font-semibold px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand/90 disabled:opacity-60"
                  >
                    {tplBusy ? "Guardando…" : "Guardar"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Para *</span>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)} required data-testid="exp-email-to" placeholder="reparador@taller.es" className="w-full text-xs border border-border rounded-md px-3 py-2" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Cc (opcional)</span>
            <input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="exp-email-cc" placeholder="copia@empresa.com" className="w-full text-xs border border-border rounded-md px-3 py-2" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Asunto</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="exp-email-subject" className="w-full text-xs border border-border rounded-md px-3 py-2 font-mono" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Mensaje *</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} required data-testid="exp-email-body" placeholder={`Hola,\n\nAdjunto los detalles del siniestro ${numero}.\n\nUn saludo,`} className="w-full text-xs border border-border rounded-md px-3 py-2" />
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-muted">
            <input type="checkbox" checked={attachInforme} onChange={(e) => setAttachInforme(e.target.checked)} data-testid="exp-email-attach-informe" />
            Adjuntar informe pericial (si está generado)
          </label>
          {attachedFiles.length > 0 && (
            <div className="border border-border rounded-md bg-surface/30">
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <div className="text-xs font-semibold inline-flex items-center gap-1.5">
                  <PaperPlaneTilt className="w-3 h-3 text-amber-700" weight="bold" />
                  Documentos adjuntos · {attachedFiles.length}
                  <span className="text-muted font-normal">({fmtSize(totalSize)})</span>
                </div>
                {totalSize > 25 * 1024 * 1024 && (
                  <span className="text-[10px] text-rose-700 font-semibold">⚠️ supera 25 MB</span>
                )}
              </div>
              <div className="px-3 py-2 space-y-1 max-h-32 overflow-y-auto" data-testid="email-attached-list">
                {attachedFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 truncate" title={f.filename}>{f.filename}</span>
                    <span className="text-muted text-[10px] whitespace-nowrap">{fmtSize(f.size_bytes || 0)}</span>
                    <button type="button" onClick={() => removeAttachment(f.id)} className="text-muted hover:text-rose-600" aria-label="Quitar adjunto" data-testid={`email-remove-att-${f.id}`}>
                      <X className="w-3 h-3" weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="exp-email-send" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <PaperPlaneTilt className="w-3 h-3" weight="bold" />
            {busy ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </form>
    </div>
  );
}


function AutosaveIndicator({ status, savedAt }) {
  if (status === "dirty") {
    return (
      <span className="text-[11px] text-muted inline-flex items-center gap-1" data-testid="autosave-dirty">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        <span className="hidden md:inline">Sin guardar</span>
      </span>
    );
  }
  if (status === "saving") {
    return (
      <span className="text-[11px] text-muted inline-flex items-center gap-1" data-testid="autosave-saving">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
        <span className="hidden md:inline">Guardando...</span>
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1" data-testid="autosave-saved">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="hidden md:inline">Guardado</span>
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="text-[11px] text-red-700 inline-flex items-center gap-1" data-testid="autosave-error">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        <span className="hidden md:inline">Error al guardar</span>
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="text-[11px] text-muted hidden md:inline" data-testid="autosave-idle">
        Guardado {savedAt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
      </span>
    );
  }
  return null;
}


// ===== Modal: abrir chat del expediente con personas mencionadas =====
function ExpedienteChatModal({ expediente, currentUser, onClose, onCreated }) {
  const [peritos, setPeritos] = useState([]);
  const [selected, setSelected] = useState([]);   // ids de miembros (incluye currentUser)
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/peritos").then((r) => {
      const list = (r.data || []).filter((p) => p.active !== false && p.id !== currentUser?.id);
      setPeritos(list);
    }).catch(() => setPeritos([]));
  }, [currentUser?.id]);

  const numSiniestro = expediente?.extracted_data?.numero_siniestro;
  const aseg = expediente?.extracted_data?.aseguradora || expediente?.detected_aseguradora;
  const chatName = numSiniestro
    ? `${aseg || "Siniestro"} · ${numSiniestro}`
    : (expediente?.source_filename || "Expediente").replace(/\.pdf$/i, "");

  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const filtered = peritos.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) || (p.code || "").toLowerCase().includes(s);
  });

  const insertMention = (code) => {
    const handle = `@${code}`;
    if (message.includes(handle)) return;
    setMessage((m) => (m ? `${m} ${handle} ` : `${handle} `));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const members = [currentUser.id, ...selected];
      const { data: conv } = await api.post("/chat/conversations", {
        type: "expediente",
        expediente_id: expediente.id,
        name: chatName,
        members,
      });
      // Auto-mencionar a los seleccionados si no se han mencionado en el texto
      const mentionedCodes = peritos.filter((p) => selected.includes(p.id)).map((p) => p.code);
      let body = message.trim();
      if (!body && mentionedCodes.length) {
        body = `${mentionedCodes.map((c) => `@${c}`).join(" ")} `;
      }
      if (body) {
        await api.post(`/chat/conversations/${conv.id}/messages`, {
          content: body,
          mentions: selected,
        });
      }
      onCreated?.(conv.id);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "No se pudo abrir el chat");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="exp-chat-modal">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg inline-flex items-center gap-2">
              <ChatCircleDots className="w-5 h-5 text-brand" weight="duotone" />
              Comentar este expediente
            </h2>
            <p className="text-[11px] text-muted mt-0.5 truncate">El chat referenciará: <strong>{chatName}</strong></p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground p-1"><X className="w-4 h-4" weight="bold" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {/* Buscar y elegir personas */}
          <div>
            <div className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted mb-1.5 flex items-center justify-between">
              <span>Personas a notificar ({selected.length})</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar perito..."
                className="!text-[11px] !py-1 !px-2 w-32"
                data-testid="exp-chat-search"
              />
            </div>
            <div className="border border-border rounded-md max-h-44 overflow-y-auto bg-white" data-testid="exp-chat-peritos">
              {filtered.length === 0 ? (
                <div className="p-3 text-xs text-muted text-center">Sin resultados</div>
              ) : filtered.map((p) => {
                const isOn = selected.includes(p.id);
                return (
                  <label key={p.id} className={`flex items-center gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-0 cursor-pointer ${isOn ? "bg-brand/5" : "hover:bg-slate-50"}`}>
                    <input type="checkbox" checked={isOn} onChange={() => toggle(p.id)} className="rounded border-border" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="font-mono text-[10px] text-muted">{p.code}</div>
                    </div>
                    {isOn && (
                      <button type="button" onClick={(e) => { e.preventDefault(); insertMention(p.code); }} className="text-[10px] text-brand hover:underline">+@</button>
                    )}
                  </label>
                );
              })}
            </div>
            <p className="text-[10px] text-muted mt-1">Selecciona a quién quieres que reciba notificación. Pulsa <strong>+@</strong> para añadir su mención al mensaje.</p>
          </div>

          {/* Mensaje inicial */}
          <div>
            <div className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted mb-1.5">Mensaje inicial (opcional)</div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Comparte un comentario sobre este expediente..."
              rows={3}
              className="!text-xs"
              data-testid="exp-chat-message"
            />
            <p className="text-[10px] text-muted mt-1">Usa <code>@código</code> para mencionar a alguien (ej. <code>@MHOLGADO</code>).</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="exp-chat-submit"
            className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <PaperPlaneTilt className="w-3.5 h-3.5" weight="bold" />
            {busy ? "Abriendo..." : "Abrir chat"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ===== Modal: compartir expediente con otro gabinete partner =====
const SHARE_BLOCKS = [
  { k: "datos",         label: "Datos del siniestro", desc: "Asegurado, póliza, causa, importes, etc." },
  { k: "docs",          label: "Documentos",          desc: "PDFs y adjuntos (parte, presupuestos, facturas)" },
  { k: "fotos",         label: "Fotografías",         desc: "Fotos cargadas en la pestaña Fotos" },
  { k: "valoracion",    label: "Valoración",          desc: "Grupos y partidas valoradas" },
  { k: "prexistencias", label: "Prexistencias",       desc: "Daños previos identificados" },
  { k: "informe",       label: "Informe final",       desc: "Datos para el informe pericial" },
];

function ShareExpedienteModal({ expediente, onClose }) {
  const [partners, setPartners] = useState([]);
  const [shares, setShares] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [blocks, setBlocks] = useState(["datos", "docs", "fotos"]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/partners"),
      api.get(`/expedientes/${expediente.id}/shares`),
    ]).then(([rp, rs]) => {
      const ps = (rp.data || []).filter((p) => p.status !== "disabled");
      setPartners(ps);
      setShares(rs.data || []);
      if (ps.length === 1) setPartnerId(ps[0].id);
    }).catch(() => toast.error("No se pudieron cargar los partners"));
  }, [expediente.id]);

  const toggle = (k) => setBlocks((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const submit = async () => {
    if (!partnerId) { toast.error("Selecciona un partner"); return; }
    if (blocks.length === 0) { toast.error("Selecciona al menos un bloque"); return; }
    setBusy(true);
    try {
      await api.post(`/expedientes/${expediente.id}/share`, {
        partner_id: partnerId,
        blocks,
        initial_message: message || null,
      });
      const partner = partners.find((p) => p.id === partnerId);
      toast.success(`Expediente compartido con ${partner?.name || "partner"}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al compartir");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-white rounded-lg w-full max-w-xl shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-start justify-between">
          <div>
            <h2 className="font-display font-bold text-lg inline-flex items-center gap-2">
              <ShareNetwork className="w-5 h-5 text-brand" weight="duotone" />
              Compartir con otro gabinete
            </h2>
            <p className="text-[11px] text-muted mt-0.5">El expediente se enviará al ERP del partner seleccionado.</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" weight="bold"/></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <div>
            <div className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted mb-1.5">Gabinete partner</div>
            {partners.length === 0 ? (
              <div className="text-xs text-muted bg-surface rounded p-3 border border-border">
                No tienes partners configurados. <a href="/admin/partners" className="text-brand hover:underline font-semibold">Añadir uno</a>.
              </div>
            ) : (
              <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="!text-xs" data-testid="share-partner-select">
                <option value="">— elegir partner —</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>

          <div>
            <div className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted mb-1.5">Qué se comparte ({blocks.length}/{SHARE_BLOCKS.length})</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SHARE_BLOCKS.map((b) => {
                const isOn = blocks.includes(b.k);
                return (
                  <button key={b.k} type="button" onClick={() => toggle(b.k)} data-testid={`share-block-${b.k}`}
                    className={`text-left p-3 rounded-md border transition-all ${isOn ? "border-brand bg-brand/5 ring-2 ring-brand/20" : "border-border hover:border-border-strong bg-white"}`}
                  >
                    <div className="flex items-start gap-2">
                      <input type="checkbox" checked={isOn} readOnly className="!w-3.5 !h-3.5 !p-0 !mt-0.5" />
                      <div>
                        <div className="font-semibold text-sm">{b.label}</div>
                        <div className="text-[10px] text-muted mt-0.5">{b.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted mb-1.5">Mensaje inicial (opcional)</div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="!text-xs" placeholder="Hola, te paso este expediente para que..." data-testid="share-message" />
          </div>

          {shares.length > 0 && (
            <div className="border-t border-border pt-4">
              <div className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted mb-2">Comparticiones existentes</div>
              <div className="space-y-2">
                {shares.map((s) => (
                  <ShareThread key={s.id} share={s} expedienteId={expediente.id} onResync={async () => {
                    try {
                      await api.post(`/shared/${s.id}/resync`);
                      toast.success("Re-sincronizado con éxito");
                      const rs = await api.get(`/expedientes/${expediente.id}/shares`);
                      setShares(rs.data || []);
                    } catch (e) { toast.error(e.response?.data?.detail || "Error al re-sincronizar"); }
                  }} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button onClick={onClose} disabled={busy} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button onClick={submit} disabled={busy || partners.length === 0} data-testid="share-submit" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <ShareNetwork className="w-3.5 h-3.5" weight="bold" />
            {busy ? "Enviando..." : "Compartir"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ===== Hilo de comentarios bidireccional dentro del modal de compartición =====
function ShareThread({ share, expedienteId, onResync }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const lastIdRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/shared/${share.id}/comments`);
      setComments(r.data || []);
    } catch (err) {
      // Polling de comentarios — no molestamos al usuario; sólo log en debug.
      if (process.env.NODE_ENV === "development") console.debug("[review] poll comments:", err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    load();
    // poll cada 6 s mientras esté abierto
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open]);

  // detectar nuevo inbound para flash
  useEffect(() => {
    if (!comments.length) return;
    const last = comments[comments.length - 1];
    if (last.direction === "inbound" && last.id !== lastIdRef.current) {
      lastIdRef.current = last.id;
    }
  }, [comments]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/shared/${share.id}/comment`, { content: draft.trim() });
      setComments((p) => [...p, r.data]);
      setDraft("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el comentario");
    } finally { setSending(false); }
  };

  const directionBadge = share.direction === "outbound"
    ? "bg-blue-50 text-blue-700 border-blue-200"
    : "bg-purple-50 text-purple-700 border-purple-200";

  return (
    <div className="bg-surface border border-border rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="text-xs">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold mr-2 border ${directionBadge}`}>
            {share.direction === "outbound" ? "Enviado a" : "Recibido de"}
          </span>
          <strong>{share.partner_name}</strong>
          <span className="text-muted ml-2">· {(share.blocks || []).join(", ")}</span>
          {share.last_synced_at && <span className="text-muted ml-2 text-[10px]">· última sync {new Date(share.last_synced_at).toLocaleString("es-ES")}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${share.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : share.status === "error" ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            {share.status}
          </span>
          {share.direction === "outbound" && share.status === "active" && (
            <button onClick={onResync} className="text-[10px] px-2 py-0.5 rounded border border-border hover:border-border-strong bg-white" title="Re-enviar el estado actual al partner" data-testid={`share-resync-${share.id}`}>
              ↻ Re-sync
            </button>
          )}
          <button onClick={() => setOpen(!open)} data-testid={`share-thread-toggle-${share.id}`} className="text-[10px] px-2 py-0.5 rounded border border-border hover:border-border-strong bg-white">
            {open ? "Cerrar" : "Comentarios"}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-white">
          <div className="max-h-60 overflow-y-auto p-3 space-y-2">
            {loading && comments.length === 0 ? (
              <div className="text-[11px] text-muted text-center py-4">Cargando comentarios...</div>
            ) : comments.length === 0 ? (
              <div className="text-[11px] text-muted text-center py-4">Sin comentarios todavía. Empieza la conversación con el partner.</div>
            ) : comments.map((c) => {
              const mine = c.direction === "outbound";
              return (
                <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs ${mine ? "bg-brand text-white" : "bg-amber-50 border border-amber-200 text-foreground"}`}>
                    <div className={`text-[10px] font-semibold mb-0.5 ${mine ? "text-white/80" : "text-amber-700"}`}>{c.author_name}</div>
                    <div className="whitespace-pre-wrap break-words">{c.content}</div>
                    <div className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-muted"}`}>{new Date(c.created_at).toLocaleString("es-ES")}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-border p-2 flex gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
              placeholder="Escribe un comentario para el partner... (Ctrl+Enter para enviar)"
              rows={2}
              className="!text-xs flex-1"
              disabled={share.status !== "active" || sending}
              data-testid={`share-comment-input-${share.id}`}
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending || share.status !== "active"}
              data-testid={`share-comment-send-${share.id}`}
              className="btn-brand text-xs px-3 rounded-md font-semibold disabled:opacity-50"
            >
              {sending ? "..." : "Enviar"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
